import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import io from "socket.io-client";
import { API_BASE, SOCKET_URL } from "../config/api";
import { useAuth } from "./AuthContext";

const CART_STORAGE_KEY = "isaac_booking_cart";

const CartContext = createContext();

const normalizeAssignment = (assignment = {}) => {
  const service = assignment.service || {};
  const employee = assignment.employee || {};
  const serviceId = Number(assignment.serviceId || service.id);
  const employeeId = Number(assignment.employeeId || employee.id);

  if (!serviceId || !employeeId) {
    return null;
  }

  return {
    serviceId,
    employeeId,
    categoryId: assignment.categoryId ?? service.categoryId ?? null,
    service: {
      id: serviceId,
      name: service.name || "",
      price: Number(service.price || 0),
      duration: Number(service.duration || 0),
      combinable: Boolean(service.combinable),
      categoryId: assignment.categoryId ?? service.categoryId ?? null,
    },
    employee: {
      id: employeeId,
      name: employee.name || "",
    },
  };
};

const normalizeCartGroups = (groups = []) =>
  groups
    .map((group, index) => {
      const assignments = (group?.assignments || group?.selections || [])
        .map(normalizeAssignment)
        .filter(Boolean);

      if (!assignments.length) {
        return null;
      }

      return {
        id: group?.id || `cart-group-${Date.now()}-${index + 1}`,
        assignments,
      };
    })
    .filter(Boolean);

const readLocalCartGroups = () => {
  try {
    const storedValue = localStorage.getItem(CART_STORAGE_KEY);

    if (!storedValue) {
      return [];
    }

    return normalizeCartGroups(JSON.parse(storedValue));
  } catch (error) {
    console.error("readLocalCartGroups:", error);
    return [];
  }
};

const buildGroupsSignature = (groups = []) =>
  JSON.stringify(
    groups.map((group) => ({
      id: group.id,
      assignments: (group.assignments || []).map((assignment) => ({
        serviceId: Number(assignment.serviceId || assignment.service?.id),
        employeeId: Number(assignment.employeeId || assignment.employee?.id),
      })),
    }))
  );

const mergeCartGroups = (baseGroups = [], extraGroups = []) => {
  const merged = [...normalizeCartGroups(baseGroups)];
  const seen = new Set(merged.map((group) => buildGroupsSignature([group])));

  normalizeCartGroups(extraGroups).forEach((group) => {
    const signature = buildGroupsSignature([group]);
    if (seen.has(signature)) {
      return;
    }

    seen.add(signature);
    merged.push(group);
  });

  return merged;
};

export const CartProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [cartGroups, setCartGroups] = useState([]);
  const [hydrated, setHydrated] = useState(false);
  const [remoteReady, setRemoteReady] = useState(false);
  const remoteUserIdRef = useRef(null);
  const cartGroupsRef = useRef([]);

  useEffect(() => {
    setCartGroups(readLocalCartGroups());
    setHydrated(true);
  }, []);

  useEffect(() => {
    cartGroupsRef.current = cartGroups;
  }, [cartGroups]);

  const refreshCartAssignments = useCallback(async () => {
    const localGroups = readLocalCartGroups();
    const sourceGroups = cartGroupsRef.current.length
      ? cartGroupsRef.current
      : localGroups;

    if (!sourceGroups.length) {
      return;
    }

    try {
      const [servicesResponse, employeesResponse] = await Promise.all([
        fetch(`${API_BASE}/services`),
        fetch(`${API_BASE}/employees`),
      ]);

      const servicesPayload = await servicesResponse.json().catch(() => []);
      const employeesPayload = await employeesResponse.json().catch(() => []);

      if (!servicesResponse.ok || !employeesResponse.ok) {
        throw new Error("No se pudo refrescar el carrito");
      }

      const servicesMap = new Map(
        (Array.isArray(servicesPayload) ? servicesPayload : []).map((service) => [
          Number(service.id),
          service,
        ])
      );

      const employeesMap = new Map(
        (Array.isArray(employeesPayload) ? employeesPayload : []).map((employee) => [
          Number(employee.id),
          employee,
        ])
      );

      const nextGroups = sourceGroups
        .map((group) => {
          const assignments = (group.assignments || [])
            .map((assignment) => {
              const serviceId = Number(assignment.serviceId || assignment.service?.id);
              const employeeId = Number(assignment.employeeId || assignment.employee?.id);
              const service = servicesMap.get(serviceId);
              const employee = employeesMap.get(employeeId);
              const employeeServices = Array.isArray(employee?.services) ? employee.services : [];
              const employeeCanDoService = employeeServices.some(
                (employeeService) => Number(employeeService.id) === serviceId
              );
              const available = Boolean(service && employee?.active && employeeCanDoService);

              if (!service || !employee) {
                return normalizeAssignment({
                  ...assignment,
                  service: {
                    ...(assignment.service || {}),
                    id: serviceId,
                    name: assignment.service?.name || "Servicio no disponible",
                    price: Number(assignment.service?.price || 0),
                    duration: Number(assignment.service?.duration || 0),
                    categoryId:
                      assignment.categoryId ?? assignment.service?.categoryId ?? null,
                    combinable: Boolean(assignment.service?.combinable),
                  },
                  employee: {
                    ...(assignment.employee || {}),
                    id: employeeId,
                    name: assignment.employee?.name || "Profesional no disponible",
                  },
                  available: false,
                });
              }

              return {
                ...normalizeAssignment({
                  ...assignment,
                  categoryId: service?.SubCategory?.categoryId ?? assignment.categoryId ?? service.categoryId ?? null,
                  service: {
                    ...service,
                    categoryId:
                      service?.SubCategory?.categoryId ??
                      assignment.categoryId ??
                      service.categoryId ??
                      null,
                  },
                  employee,
                }),
                available,
              };
            })
            .filter(Boolean);

          if (!assignments.length) {
            return null;
          }

          return {
            ...group,
            assignments,
          };
        })
        .filter(Boolean);

      setCartGroups((currentGroups) => {
        const currentSerialized = JSON.stringify(currentGroups);
        const nextSerialized = JSON.stringify(nextGroups);
        return currentSerialized === nextSerialized ? currentGroups : nextGroups;
      });
    } catch (error) {
      console.error("refreshCartAssignments:", error);
    }
  }, []);

  useEffect(() => {
    if (!hydrated) {
      return;
    }

    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartGroups));
  }, [cartGroups, hydrated]);

  useEffect(() => {
    if (!hydrated) {
      return;
    }

    refreshCartAssignments();
  }, [hydrated, refreshCartAssignments]);

  useEffect(() => {
    if (!hydrated || authLoading) {
      return;
    }

    const token = localStorage.getItem("token");

    if (!user?.id || !token) {
      remoteUserIdRef.current = null;
      setRemoteReady(false);
      return;
    }

    if (remoteUserIdRef.current === user.id) {
      setRemoteReady(true);
      return;
    }

    let isMounted = true;

    const syncInitialCart = async () => {
      try {
        const response = await fetch(`${API_BASE}/cart`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const payload = await response.json().catch(() => ({
          bookingGroups: [],
        }));

        if (!response.ok) {
          throw new Error(payload?.message || "No se pudo obtener el carrito");
        }

        if (!isMounted) {
          return;
        }

        const remoteGroups = normalizeCartGroups(payload?.bookingGroups || []);
        const localGroups = readLocalCartGroups();
        const mergedGroups = mergeCartGroups(remoteGroups, localGroups);

        setCartGroups(mergedGroups);

        if (buildGroupsSignature(mergedGroups) !== buildGroupsSignature(remoteGroups)) {
          await fetch(`${API_BASE}/cart`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              bookingGroups: mergedGroups,
            }),
          });
        }

        remoteUserIdRef.current = user.id;
        setRemoteReady(true);
      } catch (error) {
        console.error("syncInitialCart:", error);
        remoteUserIdRef.current = user.id;
        setRemoteReady(true);
      }
    };

    syncInitialCart();

    return () => {
      isMounted = false;
    };
  }, [user?.id, authLoading, hydrated]);

  useEffect(() => {
    if (!hydrated || !remoteReady || !user?.id) {
      return;
    }

    const token = localStorage.getItem("token");

    if (!token) {
      return;
    }

    const syncRemoteCart = async () => {
      try {
        await fetch(`${API_BASE}/cart`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            bookingGroups: cartGroups,
          }),
        });
      } catch (error) {
        console.error("syncRemoteCart:", error);
      }
    };

    syncRemoteCart();
  }, [cartGroups, hydrated, remoteReady, user?.id]);

  useEffect(() => {
    if (!hydrated) {
      return;
    }

    const socket = io(SOCKET_URL);

    const handleCatalogUpdate = () => {
      refreshCartAssignments();
    };

    const handleEmployeesUpdate = () => {
      refreshCartAssignments();
    };

    socket.on("catalog:updated", handleCatalogUpdate);
    socket.on("employees:updated", handleEmployeesUpdate);

    return () => {
      socket.off("catalog:updated", handleCatalogUpdate);
      socket.off("employees:updated", handleEmployeesUpdate);
      socket.disconnect();
    };
  }, [hydrated, refreshCartAssignments]);

  const addBookingGroups = (groups = []) => {
    const normalizedGroups = normalizeCartGroups(groups);

    if (!normalizedGroups.length) {
      return;
    }

    setCartGroups((currentValue) => [...currentValue, ...normalizedGroups]);
  };

  const removeBookingGroup = (groupId) => {
    setCartGroups((currentValue) =>
      currentValue.filter((group) => group.id !== groupId)
    );
  };

  const clearCart = async () => {
    setCartGroups([]);
    localStorage.removeItem(CART_STORAGE_KEY);
    localStorage.removeItem("pendingCartCheckout");

    const token = localStorage.getItem("token");
    if (!user?.id || !token) {
      return;
    }

    try {
      await fetch(`${API_BASE}/cart`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (error) {
      console.error("clearCart:", error);
    }
  };

  const cartCount = cartGroups.reduce(
    (total, group) => total + (group.assignments?.length || 0),
    0
  );

  return (
    <CartContext.Provider
      value={{
        cartGroups,
        cartCount,
        addBookingGroups,
        removeBookingGroup,
        clearCart,
        refreshCartAssignments,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
